import { cn } from "@headerhawk/ui";
import type { UsageStatus } from "@/lib/api/client";
import { formatReportCount } from "@/lib/hooks/use-usage";
import { formatPeriodBoundary } from "@/lib/utils/format-date";

export interface UsageMeterProps {
  reportCount: number;
  limit: number;
  percentUsed: number;
  periodStart: string;
  periodEnd: string;
  status: UsageStatus;
  compact?: boolean;
}

/**
 * Get color classes based on usage status
 */
function getStatusColors(status: UsageStatus) {
  switch (status) {
    case "under":
      return {
        bar: "bg-green-500",
        text: "text-green-600 dark:text-green-400",
      };
    case "warning":
      return {
        bar: "bg-yellow-500",
        text: "text-yellow-600 dark:text-yellow-400",
      };
    case "soft_exceeded":
      return {
        bar: "bg-orange-500",
        text: "text-orange-600 dark:text-orange-400",
      };
    case "hard_exceeded":
      return {
        bar: "bg-red-500",
        text: "text-red-600 dark:text-red-400",
      };
  }
}

/**
 * Get status icon based on usage status
 */
function getStatusIcon(status: UsageStatus): string {
  switch (status) {
    case "under":
      return "";
    case "warning":
      return "";
    case "soft_exceeded":
      return "";
    case "hard_exceeded":
      return "";
  }
}

export function UsageMeter({
  reportCount,
  limit,
  percentUsed,
  periodStart,
  periodEnd,
  status,
  compact = false,
}: UsageMeterProps) {
  const colors = getStatusColors(status);
  const statusIcon = getStatusIcon(status);

  // Cap visual percentage at 100% for the bar display
  const displayPercent = Math.min(percentUsed, 100);

  const usageText = `${formatReportCount(reportCount)} / ${formatReportCount(limit)}`;
  // What the meter counts, named with the day it started counting (task
  // 453): "17 / 10,000,000" beside pages counting thousands read as broken,
  // because those pages count every report kept, from before this period.
  const since = formatPeriodBoundary(periodStart);

  if (compact) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            Billable reports since {since}
          </span>
          <span className={cn("font-medium", colors.text)}>
            {statusIcon}
            {percentUsed}%
          </span>
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={percentUsed}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Billable reports since ${since}: ${usageText}`}
        >
          <div
            className={cn("h-full rounded-full transition-all", colors.bar)}
            style={{ width: `${displayPercent}%` }}
          />
        </div>
        <div className="text-xs text-muted-foreground">{usageText}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          Billable reports this period
        </span>
        <span className={cn("text-sm font-medium", colors.text)}>
          {statusIcon}
          {percentUsed}%
        </span>
      </div>

      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percentUsed}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Billable reports this period: ${usageText}`}
      >
        <div
          className={cn("h-full rounded-full transition-all", colors.bar)}
          style={{ width: `${displayPercent}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{usageText}</span>
        <span>
          Since {since} · resets{" "}
          {formatPeriodBoundary(periodEnd, { year: true })}
        </span>
      </div>
    </div>
  );
}
